// Reading legacy VÉRO sources into a raw legacy state object — read-only, never guessing
// silently. Supported inputs:
//   1. plain backup JSON from any generation (wrapped in state|data|payload|backup|snapshot|exportData|content, or bare)
//   2. bare client array
//   3. .ICRM secure backup (AES-GCM, needs the password)
//   4. base64-encoded JSON (older export path)
//   5. VÉRO Legacy Exporter bundle (tools/legacy-exporter): raw localStorage + IndexedDB dump of a browser profile
import { decryptLegacy, isLegacySecure } from './crypto.ts';

export class LegacySourceError extends Error {
  code: 'invalid_json' | 'password_required' | 'bad_password' | 'unsupported';
  constructor(code: LegacySourceError['code']) { super(code); this.code = code; }
}

export interface LegacyCandidate {
  /** where it came from, e.g. "localStorage:diamond_crm_rebuild_v1" or "file" */
  origin: string;
  state: Record<string, unknown>;
  clients: number;
  tasks: number;
  updatedHint?: string;
}

export interface LegacySource {
  format: 'plain' | 'secure' | 'base64' | 'client-array' | 'exporter-bundle';
  /** exporter bundles may hold several historical state generations; candidates[0] is the default choice */
  candidates: LegacyCandidate[];
  /** v600–v602 advisor-flow notes found outside the state (localStorage / IndexedDB) */
  externalNotes: unknown[];
  /** other IndexedDB content (e.g. VERO_V310_FILES weekly-fund files) — preserved as records */
  extraRecords: { title: string; payload: unknown }[];
  /** metadata from the wrapper (exportedAt, version label) */
  wrapper: { exportedAt?: string; version?: string };
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Port of legacy extractBackupState(): breadth-first search for the CRM state inside any wrapper. */
export function findState(parsed: unknown): Record<string, unknown> | null {
  const queue: unknown[] = [parsed];
  const seen = new Set<unknown>();
  while (queue.length) {
    const cur = queue.shift();
    if (!cur || typeof cur !== 'object' || seen.has(cur)) continue;
    seen.add(cur);
    if (Array.isArray(cur)) {
      if (cur.length && cur.every((x) => isObj(x)) && cur.some((x) => 'name' in (x as object) || 'fullName' in (x as object) || 'contracts' in (x as object))) return { clients: cur };
      continue;
    }
    const o = cur as Record<string, unknown>;
    if (Array.isArray(o.clients) || Array.isArray(o.tasks) || isObj(o.advisor) || (isObj(o.settings) && ('contractCatalog' in (o.settings as object))) ) return o;
    for (const k of ['state', 'data', 'payload', 'backup', 'snapshot', 'exportData', 'content']) if (o[k]) queue.push(o[k]);
    for (const v of Object.values(o)) if (v && typeof v === 'object') queue.push(v);
  }
  return null;
}

function parseLoose(text: string): unknown {
  const s = text.trim().replace(/^﻿/, '');
  try { return JSON.parse(s); } catch { /* try base64 */ }
  try { return JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(s), (c) => c.charCodeAt(0)))); } catch { /* fallthrough */ }
  throw new LegacySourceError('invalid_json');
}

function candidate(origin: string, state: Record<string, unknown>): LegacyCandidate {
  const clients = Array.isArray(state.clients) ? state.clients.length : 0;
  const tasks = Array.isArray(state.tasks) ? state.tasks.length : 0;
  const meta = isObj(state.meta) ? state.meta : {};
  return { origin, state, clients, tasks, updatedHint: String((meta as Record<string, unknown>).updatedAt ?? state.lastSaved ?? state.updatedAt ?? '') || undefined };
}

// Precedence of historical storage keys (newest generation first). v910 writes APP_KEY.
export const LEGACY_STATE_KEYS = [
  'diamond_crm_rebuild_v1', 'veroCRMState', 'VERO_STATE_V255', 'VERO_STATE_V250', 'diamond_crm_v1_clean', 'vero_state_v134',
  'vero_crm_state', 'vero_crm_state_v72', 'insuranceCRMState', 'insuranceCrmState', 'insurance_crm_diamond_state', 'veroState', 'VERO_V300_MANUAL_SNAPSHOT',
];

export interface ExporterBundle {
  format: 'vero-legacy-export';
  version: number;
  exportedAt?: string;
  origin?: string;
  localStorage: Record<string, string>;
  indexedDB?: Record<string, Record<string, Record<string, unknown>>>;
}
const isBundle = (v: unknown): v is ExporterBundle => isObj(v) && v.format === 'vero-legacy-export' && isObj(v.localStorage);

function fromBundle(b: ExporterBundle): LegacySource {
  const cands: LegacyCandidate[] = [];
  const tryParse = (s: unknown) => { if (typeof s !== 'string') return s; try { return JSON.parse(s); } catch { return null; } };
  const mode = b.localStorage['diamond_crm_rebuild_v1_mode'];
  const idbState = tryParse(b.indexedDB?.diamond_crm_db?.kv?.diamond_crm_rebuild_v1);
  const lsState = tryParse(b.localStorage['diamond_crm_rebuild_v1']);
  // v910 rule: mode==='idb' → IndexedDB copy is authoritative, else localStorage (then IndexedDB)
  const primary = mode === 'idb' ? [['indexedDB:diamond_crm_db/diamond_crm_rebuild_v1', idbState], ['localStorage:diamond_crm_rebuild_v1', lsState]] : [['localStorage:diamond_crm_rebuild_v1', lsState], ['indexedDB:diamond_crm_db/diamond_crm_rebuild_v1', idbState]];
  for (const [origin, st] of primary) { const s = findState(st); if (s) cands.push(candidate(origin as string, s)); }
  for (const key of LEGACY_STATE_KEYS.slice(1)) {
    const s = findState(tryParse(b.localStorage[key]));
    if (s) cands.push(candidate(`localStorage:${key}`, s));
  }
  const externalNotes: unknown[] = [];
  const ls = tryParse(b.localStorage['vero.v600.notes']);
  if (Array.isArray(ls)) externalNotes.push(...ls);
  const idbNotes = tryParse(b.indexedDB?.VERO_CRM_NOTES_DB?.kv?.notes);
  if (Array.isArray(idbNotes)) externalNotes.push(...idbNotes);
  if (!cands.length) throw new LegacySourceError('unsupported');
  const extraRecords: { title: string; payload: unknown }[] = [];
  for (const [db, stores] of Object.entries(b.indexedDB ?? {})) {
    if (db === 'diamond_crm_db' || db === 'VERO_CRM_NOTES_DB') continue;
    for (const [store, items] of Object.entries(stores ?? {})) for (const [k, v] of Object.entries(items ?? {})) extraRecords.push({ title: `idb:${db}/${store}/${k}`, payload: v });
  }
  return { format: 'exporter-bundle', candidates: cands, externalNotes, extraRecords, wrapper: { exportedAt: b.exportedAt, version: 'browser profile' } };
}

export async function readLegacySource(text: string, password?: string): Promise<LegacySource> {
  let parsed = parseLoose(text);
  let format: LegacySource['format'] = 'plain';
  if (isLegacySecure(parsed)) {
    if (!password) throw new LegacySourceError('password_required');
    try { parsed = parseLoose(await decryptLegacy(parsed, password)); } catch (e) { if (e instanceof LegacySourceError) throw e; throw new LegacySourceError('bad_password'); }
    format = 'secure';
  }
  if (isBundle(parsed)) return fromBundle(parsed);
  if (Array.isArray(parsed)) format = 'client-array';
  const state = findState(parsed);
  if (!state) throw new LegacySourceError('unsupported');
  const wrapper = isObj(parsed) ? { exportedAt: typeof parsed.exportedAt === 'string' ? parsed.exportedAt : undefined, version: typeof parsed.version === 'string' ? parsed.version : undefined } : {};
  return { format, candidates: [candidate('file', state)], externalNotes: [], extraRecords: [], wrapper };
}
